import React, {useEffect, useState} from 'react'
import {shallowEqual, useDispatch, useSelector} from 'react-redux'
import {Col, Row, Spinner, Table} from 'react-bootstrap'
import moment from 'moment'
import Swal from 'sweetalert2'
import {
  createHotelAdvancePayment,
  deleteHotelAdvancePayment,
  updateHotelAdvancePayment,
} from '../../../../setup/redux/bookings/actions'
import {getCurrencyDropdown} from '../../../../setup/redux/suppliers/actions'
import {RootState} from '../../../../setup'
import {getRequest} from '../../../../setup/redux/common/cruds'

interface AdvancePayment {
  id: number
  amount: number
  currency: string
  paid_date: string
  note?: string | null
  // Supplied by the API, which converts each payment into the hotel's net currency so the
  // conversion rule lives in one place rather than being reimplemented here.
  converted_amount?: number
  converted_currency?: string
  currency_mismatch?: boolean
}

interface Props {
  hotelBookingId: number
  service: 'hotel' | 'custom_hotel'
  netAmount: number
  netCurrency: string
}

export const HotelAdvancePaymentsTable: React.FC<Props> = ({
  hotelBookingId,
  service,
  netAmount,
  netCurrency,
}) => {
  const dispatch = useDispatch()
  const {currenciesDropdown}: any = useSelector<RootState>(
    (state) => ({currenciesDropdown: state.suppliers.currenciesDropdown}),
    shallowEqual
  )
  const API_URL = process.env.REACT_APP_API_BASE_URL

  const emptyForm = {amount: '', currency: netCurrency || 'USD', paid_date: '', note: ''}

  const [payments, setPayments] = useState<AdvancePayment[]>([])
  const [loading, setLoading] = useState(true)
  const [showAddForm, setShowAddForm] = useState(false)
  const [addForm, setAddForm] = useState(emptyForm)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editForm, setEditForm] = useState(emptyForm)

  useEffect(() => {
    dispatch(getCurrencyDropdown())
  }, [])

  // The converted amounts are computed by the API, so mutations refetch rather than splicing
  // the create/update response into state -- those responses carry no converted_amount, and
  // using them would quietly drop the balance back to unconverted figures.
  const loadPayments = () =>
    getRequest(`${API_URL}bookings/hotel-advance-payments/${service}/${hotelBookingId}`).then(
      (res: any) => setPayments(res.data?.data ?? [])
    )

  useEffect(() => {
    setLoading(true)
    loadPayments().finally(() => setLoading(false))
  }, [hotelBookingId, service])

  // Sum in the net currency. Falling back to the raw amount keeps older API responses working.
  const totalPaid = payments.reduce((sum, p) => sum + Number(p.converted_amount ?? p.amount), 0)
  const balanceDue = (Number(netAmount) || 0) - totalPaid

  // A payment in another currency for exactly the net amount is almost always the full bill
  // saved with the wrong currency, not a real cross-currency payment. Worth a word rather than
  // silently reporting the hotel as unpaid once conversion is applied.
  const rawTotal = payments.reduce((sum, p) => sum + Number(p.amount), 0)
  const hasMismatch = payments.some((p) => p.currency_mismatch)
  const looksMislabelled = hasMismatch && Math.abs(rawTotal - (Number(netAmount) || 0)) < 0.01

  const handleAdd = async () => {
    if (!addForm.amount || !addForm.paid_date) return
    const dto: any = {
      amount: Number(addForm.amount),
      currency: addForm.currency || 'USD',
      paid_date: addForm.paid_date,
      note: addForm.note || undefined,
    }
    if (service === 'hotel') dto.booking_has_hotel_id = hotelBookingId
    else dto.booking_has_custom_hotel_service_id = hotelBookingId

    const result: any = await dispatch(createHotelAdvancePayment(dto))
    if (result?.data) {
      await loadPayments()
      setAddForm(emptyForm)
      setShowAddForm(false)
    }
  }

  const handleEditSave = async (id: number) => {
    if (!editForm.amount || !editForm.paid_date) return
    const dto = {
      amount: Number(editForm.amount),
      currency: editForm.currency || 'USD',
      paid_date: editForm.paid_date,
      note: editForm.note || undefined,
    }
    const result: any = await dispatch(updateHotelAdvancePayment(id, dto))
    if (result?.data) {
      await loadPayments()
      setEditingId(null)
    }
  }

  const handleDelete = (id: number) => {
    Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
    }).then(async (result) => {
      if (result.isConfirmed) {
        await dispatch(deleteHotelAdvancePayment(id))
        setPayments((prev) => prev.filter((p) => p.id !== id))
      }
    })
  }

  const startEdit = (p: AdvancePayment) => {
    setEditingId(p.id)
    setEditForm({
      amount: String(p.amount),
      currency: p.currency || 'USD',
      paid_date: String(p.paid_date).substring(0, 10),
      note: p.note ?? '',
    })
  }

  if (loading) {
    return (
      <div className='text-center py-3'>
        <Spinner animation='border' size='sm' /> Loading payments...
      </div>
    )
  }

  return (
    <div className='mt-2'>
      <div className='d-flex justify-content-between align-items-center mb-2'>
        <strong>Advance Payments</strong>
        {!showAddForm && (
          <button
            type='button'
            className='btn btn-sm btn-outline-primary'
            onClick={() => setShowAddForm(true)}
          >
            + Add Payment
          </button>
        )}
      </div>

      {payments.length > 0 && (
        <Table striped bordered size='sm' className='mb-2'>
          <thead>
            <tr>
              <th>Date</th>
              <th>Amount</th>
              <th>Currency</th>
              <th>Note</th>
              <th style={{width: 100, whiteSpace: 'nowrap'}}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {payments.map((p) => (
              <tr key={p.id}>
                {editingId === p.id ? (
                  <>
                    <td>
                      <input
                        type='date'
                        className='form-control form-control-sm'
                        value={editForm.paid_date}
                        onChange={(e) => setEditForm((f) => ({...f, paid_date: e.target.value}))}
                      />
                    </td>
                    <td>
                      <input
                        type='number'
                        className='form-control form-control-sm'
                        value={editForm.amount}
                        placeholder='0.00'
                        onChange={(e) => setEditForm((f) => ({...f, amount: e.target.value}))}
                      />
                    </td>
                    <td>
                      <select
                        className='form-control form-control-sm form-select'
                        value={editForm.currency}
                        onChange={(e) => setEditForm((f) => ({...f, currency: e.target.value}))}
                      >
                        {currenciesDropdown.map((c: any) => (
                          <option key={c.name} value={c.name}>
                            {c.name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <input
                        type='text'
                        className='form-control form-control-sm'
                        value={editForm.note}
                        placeholder='Note (optional)'
                        onChange={(e) => setEditForm((f) => ({...f, note: e.target.value}))}
                      />
                    </td>
                    <td>
                      <button
                        type='button'
                        className='btn btn-sm btn-success me-1'
                        onClick={() => handleEditSave(p.id)}
                      >
                        ✓
                      </button>
                      <button
                        type='button'
                        className='btn btn-sm btn-secondary'
                        onClick={() => setEditingId(null)}
                      >
                        ✕
                      </button>
                    </td>
                  </>
                ) : (
                  <>
                    <td>{moment(p.paid_date).format('DD MMM YYYY')}</td>
                    <td>
                      {Number(p.amount).toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </td>
                    <td>
                      {p.currency}
                      {p.currency_mismatch && (
                        <span
                          className='badge badge-light-warning ms-2'
                          title={`Converted to ${Number(p.converted_amount ?? 0).toLocaleString(
                            'en-US',
                            {minimumFractionDigits: 2, maximumFractionDigits: 2}
                          )} ${p.converted_currency ?? netCurrency} for the balance`}
                        >
                          ≠ {p.converted_currency ?? netCurrency}
                        </span>
                      )}
                    </td>
                    <td>{p.note ?? '-'}</td>
                    <td style={{whiteSpace: 'nowrap'}}>
                      <button
                        type='button'
                        className='btn btn-sm btn-light me-1'
                        onClick={() => startEdit(p)}
                      >
                        <i className='fa fa-edit text-primary' />
                      </button>
                      <button
                        type='button'
                        className='btn btn-sm btn-light'
                        onClick={() => handleDelete(p.id)}
                      >
                        <i className='fa fa-trash text-danger' />
                      </button>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {payments.length === 0 && !showAddForm && (
        <p className='text-muted small mb-2'>No advance payments recorded.</p>
      )}

      {showAddForm && (
        <div className='border rounded p-3 mb-2 bg-light'>
          <Row className='g-2 mb-2'>
            <Col md={4}>
              <label className='form-label form-label-sm mb-1'>Date</label>
              <input
                type='date'
                className='form-control form-control-sm'
                value={addForm.paid_date}
                onChange={(e) => setAddForm((f) => ({...f, paid_date: e.target.value}))}
              />
            </Col>
            <Col md={4}>
              <label className='form-label form-label-sm mb-1'>Amount</label>
              <input
                type='number'
                className='form-control form-control-sm'
                value={addForm.amount}
                placeholder='0.00'
                onChange={(e) => setAddForm((f) => ({...f, amount: e.target.value}))}
              />
            </Col>
            <Col md={4}>
              <label className='form-label form-label-sm mb-1'>Currency</label>
              <select
                className='form-control form-control-sm form-select'
                value={addForm.currency}
                onChange={(e) => setAddForm((f) => ({...f, currency: e.target.value}))}
              >
                {currenciesDropdown.map((c: any) => (
                  <option key={c.name} value={c.name}>
                    {c.name}
                  </option>
                ))}
              </select>
            </Col>
          </Row>
          <Row className='g-2 align-items-end'>
            <Col md={8}>
              <label className='form-label form-label-sm mb-1'>
                Note <span className='text-muted'>(optional)</span>
              </label>
              <input
                type='text'
                className='form-control form-control-sm'
                value={addForm.note}
                placeholder='e.g. First installment'
                onChange={(e) => setAddForm((f) => ({...f, note: e.target.value}))}
              />
            </Col>
            <Col md={4} className='d-flex gap-2 justify-content-end'>
              <button type='button' className='btn btn-sm btn-primary' onClick={handleAdd}>
                Save
              </button>
              <button
                type='button'
                className='btn btn-sm btn-secondary'
                onClick={() => {
                  setShowAddForm(false)
                  setAddForm(emptyForm)
                }}
              >
                Cancel
              </button>
            </Col>
          </Row>
        </div>
      )}

      {looksMislabelled && (
        <div className='alert alert-warning py-2 px-3 mb-2'>
          <i className='fa fa-triangle-exclamation me-2' />
          The payments total exactly {netCurrency} {Number(netAmount).toLocaleString('en-US')} but
          were recorded in a different currency. That usually means the currency was picked wrongly
          rather than a genuine cross-currency payment — please check before relying on the balance
          below.
        </div>
      )}

      <div className='text-end'>
        <span className='text-danger fw-bold fs-6'>
          Balance Due:{' '}
          {balanceDue.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}{' '}
          {netCurrency}
        </span>
      </div>
    </div>
  )
}
